import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
      jobTitle: article.authorTitle,
    },
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '明智育儿指南',
      logo: {
        '@type': 'ImageObject',
        url: 'https://aitdev.cn/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://aitdev.cn/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://aitdev.cn',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://aitdev.cn/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} | 明智育儿指南`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://aitdev.cn/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        articlePublishedTime={article.publishedAt}
        articleModifiedTime={article.publishedAt}
        schemaJson={[articleSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="pt-16">
        {/* Article Header */}
        <div className="bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900 whitespace-nowrap hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                {article.categoryName}
              </Link>
              {article.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-2 py-0.5 rounded-full text-xs bg-background-100 text-foreground-500 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              {article.title}
            </h1>

            <div className="flex items-center gap-3 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="text-foreground-300">|</span>
              <span className="whitespace-nowrap">{article.authorTitle}</span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-calendar-line"></i>
                {article.publishedAt}
              </span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line"></i>
                {article.readTime} 分钟阅读
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - 明智育儿指南`}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Body */}
          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-foreground-950
              [&_h2]:font-heading"
            style={{ '--font-heading': 'var(--font-heading)' } as React.CSSProperties}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Author Card */}
          <div className="mt-12 p-6 rounded-lg bg-background-100 border border-background-200/70 flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-3-line text-xl text-accent-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-950">{article.author}</p>
              <p className="text-xs text-foreground-500">{article.authorTitle}</p>
              <p className="text-sm text-foreground-600 mt-1 leading-relaxed">
                致力于儿童心理学研究与家庭教育实践，以科学视角帮助家长做出明智的育儿决策。
              </p>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}